import type { Logger } from '../../types/logging/api';
import type { WorkerCommandType } from './publisher.ts';
import type { PublishCommand } from './publisher/types.ts';
import { Metrics } from '../../types/metrics/api';

// common code for worker and in-process publisher execution

async function importPreload(log: Logger, module: string) {
    try {
        let preload;
        try {
            preload = await import(module);
        }
        catch (e: any) {
            if (e?.code === 'ERR_VM_DYNAMIC_IMPORT_CALLBACK_MISSING' && typeof require === 'function') {
                preload = require(module);
            }
            else {
                throw e;
            }
        }
        if (typeof preload === 'function') {
            return await preload();
        }
        if (typeof preload.default === 'function') {
            return await preload.default();
        }
    }
    catch (e) {
        log.error(`failed to load preload`, e);
    }
}

async function importHandler(log: Logger, publishFn: string, cfg: unknown) {
    log.debug(`loading publisher from ${publishFn}`);
    let factory: {
        name?: string,
        create: (cfg: unknown, logger: Logger) => Promise<(command: PublishCommand) => Promise<unknown>>;
    };
    try {
        factory = await import(publishFn);
    }
    catch (e: any) {
        if (e?.code === 'ERR_VM_DYNAMIC_IMPORT_CALLBACK_MISSING' && typeof require === 'function') {
            factory = require(publishFn);
        }
        else {
            throw e;
        }
    }
    if (typeof factory['default'] === 'object') {
        factory = factory['default'] as typeof factory;
    }
    return await factory.create(cfg, log.child(`publisher.${factory.name ?? publishFn}`));
}

export class WorkerCommandExecutor {
    readonly #log: Logger;
    #handler?: (command: PublishCommand) => Promise<unknown>;
    #cleanupArg: unknown;

    constructor(log: Logger) {
        this.#log = log;
    }

    async #startHandler(arg: WorkerCommandType['start']): Promise<void> {
        const { publishFn, cfg } = arg;
        if (cfg?.preload) {
            const result = await importPreload(this.#log, cfg.preload);
            if (typeof result === 'object') {
                for (const key in result) {
                    cfg[key] = result[key];
                }
            }
        }
        let handler: (command: PublishCommand) => Promise<unknown>;
        if (typeof publishFn === 'function') {
            handler = publishFn;
        }
        else {
            handler = await importHandler(this.#log, publishFn, cfg);
        }
        const cleanupArg = await handler('start');
        this.#handler = handler;
        this.#cleanupArg = cleanupArg;
    }

    async #stopHandler() {
        const cleanup = await this.#handler?.('stop');
        if (typeof cleanup === 'function') {
            await cleanup(this.#cleanupArg);
        }
    }

    async #handleUpdate(update: Metrics.Update) {
        if (!this.#handler) {
            throw new Error('worker not started');
        }
        await this.#handler(update);
    }

    async execute<C extends keyof WorkerCommandType>(cmd: C, arg: WorkerCommandType[C]) {
        switch (cmd) {
            case 'start': {
                await this.#startHandler(arg as WorkerCommandType['start']);
                break;
            }
            case 'stop': {
                await this.#stopHandler();
                break;
            }
            case 'update': {
                await this.#handleUpdate(arg as Metrics.Update);
                break;
            }
        }
    }
}
